import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Boxes, Package, Weight } from "lucide-react";
import i18n from "@/lib/i18n";
import { getFleetLoadReport } from "@/api/reports";
import { cn } from "@/lib/cn";
import { KpiCard } from "@/components/ui/KpiCard";
import { FleetLoadTable } from "@/features/reports/components/FleetLoadTable";
import { hasPalletData } from "@/features/reports/components/fleetLoad";
import { useVolumeUnit } from "@/hooks/useVolumeUnit";
import { formatVolumeUnit } from "@/lib/format";
import type { FleetLoadSummary, ReportParams } from "@/types/reports";

interface Props {
  /** Shared analytics filter params (date range, vehicle). */
  params: ReportParams;
}

const DASH = "—";

/**
 * Formats a finite number with locale-aware thousands grouping and at most one
 * decimal (e.g. ru "28 450" / "572,9"); em-dash for null/NaN. Mirrors the
 * thousands-separator treatment applied to the Fleet Load table cells.
 */
function formatAmount(value: number | null): string {
  if (value === null || !Number.isFinite(value)) return DASH;
  return new Intl.NumberFormat(i18n.language, {
    maximumFractionDigits: 1,
  }).format(value);
}

/**
 * Builds the "used / capacity" headline value for a KPI card. Capacity null or
 * zero → em-dash on the capacity side (Sprint 107 null-guard).
 */
function usedOverCapacity(
  used: number | null,
  capacity: number | null,
): string {
  const cap =
    capacity !== null && Number.isFinite(capacity) && capacity !== 0
      ? formatAmount(capacity)
      : DASH;
  return `${formatAmount(used)} / ${cap}`;
}

/** Avg-utilization badge shown next to the KPI value; em-dash when null. */
function UtilizationBadge({ pct }: { pct: number | null }) {
  const { t } = useTranslation();
  const label =
    pct !== null && Number.isFinite(pct) ? `${formatAmount(pct)}%` : DASH;
  return (
    <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-semibold text-gray-600">
      {t("analytics.fleet.avgUtilization")} {label}
    </span>
  );
}

/**
 * Fleet Load tab (TASK-3830): three fleet KPI cards (weight / volume / pallets,
 * each used-over-capacity with the average utilization %) over a per-vehicle
 * utilization table. Wired to `GET /api/v1/reports/fleet-load/`, respecting the
 * shared date-range / vehicle filter params.
 */
export function FleetLoadTab({ params }: Props) {
  const { t } = useTranslation();
  const volumeUnit = useVolumeUnit();
  const { data, isLoading } = useQuery({
    queryKey: ["reports", "fleet-load", params],
    queryFn: () => getFleetLoadReport(params),
  });

  const summary: FleetLoadSummary | undefined = data?.summary;
  const rows = data?.results ?? [];

  // Hide the pallet KPI card when the tenant doesn't track pallets (the whole
  // column/KPI is otherwise "0 / —" and reads as broken data). Mirrors the
  // table's column hide so the card and the column stay in sync (TASK-3848).
  const showPallets = hasPalletData(rows);

  return (
    <div className="space-y-4">
      <div
        className={cn(
          "grid grid-cols-1 gap-4",
          showPallets ? "sm:grid-cols-3" : "sm:grid-cols-2",
        )}
      >
        <KpiCard
          icon={Weight}
          label={t("analytics.fleet.weightKg")}
          value={
            summary
              ? usedOverCapacity(
                  summary.total_weight_used_kg,
                  summary.total_weight_capacity_kg,
                )
              : undefined
          }
          badge={
            summary ? (
              <UtilizationBadge pct={summary.avg_utilization_weight_pct} />
            ) : undefined
          }
        />
        <KpiCard
          icon={Boxes}
          label={t("analytics.fleet.volumeM3", {
            unit: formatVolumeUnit(volumeUnit),
          })}
          value={
            summary
              ? usedOverCapacity(
                  summary.total_volume_used_m3,
                  summary.total_volume_capacity_m3,
                )
              : undefined
          }
          badge={
            summary ? (
              <UtilizationBadge pct={summary.avg_utilization_volume_pct} />
            ) : undefined
          }
        />
        {showPallets && (
          <KpiCard
            icon={Package}
            label={t("analytics.fleet.pallets")}
            value={
              summary
                ? usedOverCapacity(
                    summary.total_pallets_used,
                    summary.total_pallets_capacity,
                  )
                : undefined
            }
            badge={
              summary ? (
                <UtilizationBadge pct={summary.avg_utilization_pallets_pct} />
              ) : undefined
            }
          />
        )}
      </div>

      <FleetLoadTable rows={rows} isLoading={isLoading} />
    </div>
  );
}
